import { useState, useRef, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';
//const FORM_URL = 'https://readdy.ai/api/form/d76vh68rnr7vi0ujqh50';
const FORM_URL = 'https://greenlyelectronicsrecycling.com/api/resend-readyai-form.php' ;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\s\-().+]{7,20}$/;

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  preferred_contact: string;
  pickup_address: string;
  items_description: string;
  pickup_instructions: string;
  data_destruction: string;
  urgency: string;
  volume: string;
  locations_count: string;
  additional_notes: string;
  website_url?: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  preferred_contact?: string;
  pickup_address?: string;
  items_description?: string;
  data_destruction?: string;
  volume?: string;
  locations_count?: string;
}

const initialForm: FormState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  preferred_contact: '',
  pickup_address: '',
  items_description: '',
  pickup_instructions: '',
  data_destruction: '',
  urgency: '',
  volume: '',
  locations_count: '',
  additional_notes: '',
  website_url: '',
};

const validate = (form: FormState): FormErrors => {
  const errors: FormErrors = {};

  if (!form.name.trim()) {
    errors.name = 'Name is required.';
  } else if (form.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters.';
  }

  if (!form.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_RE.test(form.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  if (!form.phone.trim()) {
    errors.phone = 'Phone is required.';
  } else if (!PHONE_RE.test(form.phone.trim())) {
    errors.phone = 'Please enter a valid phone number.';
  }

  if (!form.preferred_contact) {
    errors.preferred_contact = 'Please select a preferred contact method.';
  }

  if (!form.pickup_address.trim()) {
    errors.pickup_address = 'Pickup address is required.';
  } else if (form.pickup_address.trim().length < 10) {
    errors.pickup_address = 'Please enter a complete address.';
  }

  if (!form.items_description.trim()) {
    errors.items_description = 'Please describe the items you need picked up.';
  } else if (form.items_description.trim().length < 10) {
    errors.items_description = 'Please provide more details (at least 10 characters).';
  }

  if (!form.data_destruction) {
    errors.data_destruction = 'Please select a data destruction option.';
  }

  if (!form.volume) {
    errors.volume = 'Please select the volume of material.';
  }

  if (!form.locations_count) {
    errors.locations_count = 'Please select the number of locations.';
  }

  return errors;
};

const FieldError = ({ msg }: { msg?: string }) =>
  msg ? (
    <p className="flex items-center gap-1.5 text-red-500 text-xs mt-1.5">
      <i className="ri-error-warning-line shrink-0"></i>
      {msg}
    </p>
  ) : null;

const inputClass = (hasError: boolean) =>
  `w-full border rounded-md px-3 py-2.5 text-base text-gray-800 placeholder-gray-400 focus:outline-none transition-colors ${
    hasError
      ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
      : 'border-gray-300 focus:border-[#1B6B3A]'
  }`;

const PickupRequestPage = () => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const charCounts = {
    pickup_address: form.pickup_address.length,
    items_description: form.items_description.length,
    pickup_instructions: form.pickup_instructions.length,
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    setForm(updated);
    if (touched[name as keyof FormState]) {
      const newErrors = validate(updated);
      setErrors((prev) => ({ ...prev, [name]: newErrors[name as keyof FormErrors] }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const newErrors = validate(form);
    setErrors((prev) => ({ ...prev, [name]: newErrors[name as keyof FormErrors] }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError('');

    // Mark all fields touched and validate
    const allTouched: Partial<Record<keyof FormState, boolean>> = {};
    (Object.keys(form) as (keyof FormState)[]).forEach((k) => (allTouched[k] = true));
    setTouched(allTouched);

    const newErrors = validate(form);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Honeypot check — bots fill this, real users don't see it
    if (form.website_url && form.website_url.trim() !== '') {
      setSubmitted(true);
      setForm(initialForm);
      setErrors({});
      setTouched({});
      return;
    }

    setSubmitting(true);

    const params = new URLSearchParams();
    (Object.entries(form) as [string, string][]).forEach(([k, v]) => {
      if (k !== 'website_url') params.append(k, v);
    });
    params.append('page', 'pickup-request');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setForm(initialForm);
        setErrors({});
        setTouched({});
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } catch {
      setServerError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9F7] flex flex-col">
      <title>E-Waste Pickup Request | Free Business Electronics Pickup | Greenly Los Angeles</title>
      <meta
        name="description"
        content="Request a free e-waste pickup for your Los Angeles business. Greenly picks up computers, TVs, batteries & all electronics. Free data destruction included. Call (213) 600-1000."
      />
      <meta name="keywords" content="e-waste pickup Los Angeles, free electronics pickup LA, business e-waste pickup, Greenly pickup request" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/pickup-request`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${SITE_URL}/pickup-request#webpage`,
            "url": `${SITE_URL}/pickup-request`,
            "name": "E-Waste Pickup Request | Free Business Electronics Pickup | Greenly Los Angeles",
            "description": "Request a free e-waste pickup for your Los Angeles business. Greenly picks up computers, TVs, batteries & all electronics. Data destruction included. Call (213) 600-1000.",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "about": { "@id": `${SITE_URL}/#business` },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": "Pickup Request", "item": `${SITE_URL}/pickup-request` }
              ]
            }
          },
          {
            "@type": "Service",
            "@id": `${SITE_URL}/pickup-request#service`,
            "name": "Free E-Waste Pickup — Los Angeles",
            "description": "Free e-waste pickup for qualifying businesses throughout Los Angeles County. Greenly picks up computers, TVs, batteries, and all electronics. Data destruction included.",
            "url": `${SITE_URL}/pickup-request`,
            "provider": { "@id": `${SITE_URL}/#business` },
            "areaServed": { "@type": "AdministrativeArea", "name": "Los Angeles County" },
            "serviceType": "E-Waste Pickup",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "Free e-waste pickup for qualifying businesses in Los Angeles County"
            }
          }
        ]
      })}</script>
      <Navbar />

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              E-Waste Pickup Request Form
            </h1>
            <p className="text-base text-gray-500">
              Fill out the form below and our team will get back to you to schedule your pickup.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Form Card */}
            <div className="flex-1 bg-white border border-gray-200 rounded-xl p-6 md:p-8">
              {submitted ? (
                <div className="py-6">
                  {/* Header */}
                  <div className="flex flex-col items-center text-center mb-7">
                    <div className="w-16 h-16 flex items-center justify-center bg-[#1B6B3A] rounded-full mb-4">
                      <i className="ri-truck-line text-white text-3xl"></i>
                    </div>
                    <h2 className="text-xl font-extrabold text-gray-900 mb-2">Pickup Request Received!</h2>
                    <p className="text-base text-gray-500 max-w-sm">
                      Your request is in our system. Here&apos;s exactly what happens next.
                    </p>
                  </div>

                  {/* Steps */}
                  <div className="space-y-4 mb-7">
                    {[
                      {
                        icon: 'ri-mail-check-line',
                        title: 'Confirmation email on its way',
                        desc: 'Check your inbox — a copy of your request will arrive shortly.',
                      },
                      {
                        icon: 'ri-search-eye-line',
                        title: 'We review your request',
                        desc: 'Our team will look over your items, address, and data destruction needs.',
                      },
                      {
                        icon: 'ri-phone-line',
                        title: 'We call to confirm',
                        desc: 'A Greenly team member will contact you within 1 business day to schedule your pickup date and time.',
                      },
                      {
                        icon: 'ri-truck-line',
                        title: 'We show up & handle everything',
                        desc: 'Our certified crew arrives, loads your e-waste, and provides documentation.',
                      },
                    ].map((step, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 bg-[#F4FAF7] rounded-xl">
                        <div className="w-9 h-9 flex items-center justify-center bg-[#1B6B3A] rounded-full shrink-0">
                          <i className={`${step.icon} text-white text-base`}></i>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-base">{step.title}</p>
                          <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Call CTA */}
                  <div className="bg-[#1B6B3A] rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 mb-5">
                    <div>
                      <p className="text-white font-bold text-base">Need it sooner?</p>
                      <p className="text-green-200 text-sm">Call us directly to expedite your pickup.</p>
                    </div>
                    <a
                      href="tel:+12136001000"
                      className="inline-flex items-center gap-2 bg-white text-[#1B6B3A] font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-phone-fill"></i> (213) 600-1000
                    </a>
                  </div>

                  <button
                    onClick={() => setSubmitted(false)}
                    className="w-full border border-gray-300 text-gray-600 text-base font-semibold py-2.5 rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <input type="hidden" name="page" value="pickup-request" />
                  {/* Honeypot field — hidden from real users, bots will fill it */}
                  <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none', tabIndex: -1 }} aria-hidden="true">
                    <label htmlFor="website_url_hp_pickup">Website URL</label>
                    <input
                      id="website_url_hp_pickup"
                      type="text"
                      name="website_url"
                      value={form.website_url}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="John Doe"
                        className={inputClass(!!errors.name)}
                      />
                      <FieldError msg={errors.name} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="john@company.com"
                        className={inputClass(!!errors.email)}
                      />
                      <FieldError msg={errors.email} />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="(213) 000-0000"
                      className={inputClass(!!errors.phone)}
                    />
                    <FieldError msg={errors.phone} />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company <span className="text-gray-400 font-normal text-xs">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Company ABC"
                      className={inputClass(false)}
                    />
                  </div>

                  {/* Preferred Contact */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred method of contact <span className="text-red-400">*</span>
                    </label>
                    <div className="flex items-center gap-6">
                      {['Phone', 'Email'].map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="preferred_contact"
                            value={opt}
                            checked={form.preferred_contact === opt}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="accent-[#1B6B3A] w-4 h-4 cursor-pointer"
                          />
                          <span className="text-base text-gray-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                    <FieldError msg={errors.preferred_contact} />
                  </div>

                  {/* Pickup Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Pickup Address <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="pickup_address"
                      rows={3}
                      maxLength={500}
                      value={form.pickup_address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Street address, city, state, zip"
                      className={`${inputClass(!!errors.pickup_address)} resize-none`}
                    />
                    <div className="flex items-center justify-between mt-1">
                      <FieldError msg={errors.pickup_address} />
                      <p className="text-xs text-gray-400">{charCounts.pickup_address}/500</p>
                    </div>
                  </div>

                  {/* Items Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Brief description of items and estimated quantities{' '}
                      <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="items_description"
                      rows={4}
                      maxLength={500}
                      value={form.items_description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. 10 desktop computers, 5 monitors, 2 boxes of cables..."
                      className={`${inputClass(!!errors.items_description)} resize-none`}
                    />
                    <div className="flex items-center justify-between mt-1">
                      <FieldError msg={errors.items_description} />
                      <p className="text-xs text-gray-400">{charCounts.items_description}/500</p>
                    </div>
                  </div>

                  {/* Pickup Instructions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Pickup instructions{' '}
                      <span className="text-gray-400 font-normal text-xs">(optional)</span>
                    </label>
                    <textarea
                      name="pickup_instructions"
                      rows={3}
                      maxLength={500}
                      value={form.pickup_instructions}
                      onChange={handleChange}
                      placeholder="e.g. Call before arrival, items are on 2nd floor, loading dock available..."
                      className={`${inputClass(false)} resize-none`}
                    />
                    <p className="text-xs text-gray-400 text-right mt-0.5">{charCounts.pickup_instructions}/500</p>
                  </div>

                  {/* Data Destruction */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Destruction Options <span className="text-red-400">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        'Hard Drive Shredding with Certificate $10 each',
                        'Data Wipe with software for reuse — Free of charge',
                        'No Hard Drives or Backup Tapes in load',
                      ].map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="data_destruction"
                            value={opt}
                            checked={form.data_destruction === opt}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="accent-[#1B6B3A] w-4 h-4 cursor-pointer shrink-0"
                          />
                          <span className="text-base text-gray-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                    <FieldError msg={errors.data_destruction} />
                  </div>

                  {/* Urgency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      How quickly do you need service?{' '}
                      <span className="text-gray-400 font-normal text-xs">(optional)</span>
                    </label>
                    <select
                      name="urgency"
                      value={form.urgency}
                      onChange={handleChange}
                      className={`${inputClass(false)} bg-white cursor-pointer`}
                    >
                      <option value="">Select urgency...</option>
                      <option>Immediately — this is an urgent situation!</option>
                      <option>Within the next few days</option>
                      <option>Within the next week</option>
                      <option>Within the next month</option>
                      <option>No rush — flexible scheduling</option>
                    </select>
                  </div>

                  {/* Volume */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      What volume of material are we talking about? <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="volume"
                      value={form.volume}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`${inputClass(!!errors.volume)} bg-white cursor-pointer`}
                    >
                      <option value="">Select volume...</option>
                      <option>A few devices (1–5 items)</option>
                      <option>Small load (6–20 items)</option>
                      <option>Medium load (1–3 pallets)</option>
                      <option>Large load (4–10 pallets)</option>
                      <option>Very large / full truck load</option>
                    </select>
                    <FieldError msg={errors.volume} />
                  </div>

                  {/* Locations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How many locations are involved? <span className="text-red-400">*</span>
                    </label>
                    <div className="space-y-2">
                      {['1 location', '2–9 locations', '10+ locations'].map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="locations_count"
                            value={opt}
                            checked={form.locations_count === opt}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="accent-[#1B6B3A] w-4 h-4 cursor-pointer"
                          />
                          <span className="text-base text-gray-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                    <FieldError msg={errors.locations_count} />
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      What else would you like to tell us about this job?{' '}
                      <span className="text-gray-400 font-normal text-xs">(optional)</span>
                    </label>
                    <textarea
                      name="additional_notes"
                      rows={4}
                      maxLength={500}
                      value={form.additional_notes}
                      onChange={handleChange}
                      placeholder="Any other details that would help us prepare for your pickup..."
                      className={`${inputClass(false)} resize-none`}
                    />
                  </div>

                  {/* Server Error */}
                  {serverError && (
                    <div className="bg-red-50 border border-red-200 text-red-600 rounded-md px-4 py-3 flex items-center gap-3 text-sm">
                      <i className="ri-error-warning-line text-lg"></i>
                      {serverError}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#1B6B3A] hover:bg-[#155530] disabled:opacity-60 text-white text-base font-bold py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Submit Request
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar Notes */}
            <div className="w-full lg:w-64 shrink-0 space-y-4">
              <div className="bg-[#FFF3CD] border border-[#FBBF24] rounded-xl p-4">
                <p className="text-sm font-bold text-[#92400E] mb-1">Important Note:</p>
                <p className="text-sm text-[#78350F] leading-relaxed">
                  All TVs and Monitor Screens drop-off must have been used in California and require basic personal information to be collected as required by CalRecycle. Please be ready to provide your Name, Address and Phone number as they appear on your driver&apos;s license.
                </p>
              </div>
              <div className="bg-[#DCFCE7] border border-[#4CAF82] rounded-xl p-4">
                <p className="text-sm font-bold text-[#14532D] mb-1">Note:</p>
                <p className="text-sm text-[#166534] leading-relaxed">
                  Pickup Service is only available for businesses. Individual households must drop off. Thank you.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-sm font-bold text-gray-700 mb-2">Need help?</p>
                <p className="text-sm text-gray-500 mb-3 leading-relaxed">
                  Call us directly and we&apos;ll schedule your pickup over the phone.
                </p>
                <a
                  href="tel:+12136001000"
                  className="flex items-center gap-2 text-[#1B6B3A] text-base font-semibold hover:underline cursor-pointer"
                >
                  <i className="ri-phone-line"></i>
                  (213) 600-1000
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default PickupRequestPage;
